// One PostHog person per human, and nothing dropped on the floor before the
// SDK exists.
//
// Two separate faults used to break the link between a session and the person
// who owned it, and both of them are invisible from the PostHog UI — the events
// arrive, they just arrive attached to the wrong person or to nobody.
//
// 1. TWO SPELLINGS OF THE SAME PHONE NUMBER.
//    The OTP and 2FA screens identified on what the user typed
//    (`09xxxxxxxxx`); the profile page and the startup session-restore
//    identified on what the backend returns (`+989xxxxxxxxx`). posthog-js only
//    emits the `$identify` merge event when it is moving an *anonymous* visitor
//    onto a person — for an already-identified one it silently swaps
//    `distinct_id` and sends nothing. So every ordinary web login identified as
//    `09…`, absorbed the pre-login funnel, and then two screens later slid onto
//    a second, unlinked `+98…` person that collected everything afterwards.
//
// 2. IDENTIFY BEFORE INIT.
//    `ensureSessionProfile()` deliberately runs before `root.render()`, and
//    PostHog is initialised by <PostHogProvider> during that render.
//    `posthog.identify()` returns early when the library is not loaded, so on
//    the paths that matter most — mini-app auto-login, admin impersonate, any
//    returning session without a cached profile — the identify never happened
//    at all and the whole session stayed anonymous.
//
// Everything analytics-related that can run before the provider mounts goes
// through the queue here and is released by <TelemetryFlusher>.

import posthog from 'posthog-js'

import { isSameMobile, toLocalMobile } from 'utilities/phone'

type Props = Record<string, unknown>

/**
 * The canonical distinct_id for a person: their mobile in `09xxxxxxxxx` form.
 *
 * `09…` rather than `+98…` on purpose — it is the spelling the OTP screen has
 * always used, so it is the one the existing persons (and every funnel built on
 * them) are already keyed by.
 */
export const toPersonId = (phone: string | null | undefined): string | null =>
    toLocalMobile(phone) || (phone ? String(phone) : null)

let superProps: Props = {}
let queued: Array<{ name: string; props: Props }> = []
let pendingIdentity: { id: string; props: Props } | null = null
let flushed = false

/** Super-properties to attach to every later event. */
export const queueRegister = (props: Props) => {
    superProps = { ...superProps, ...props }
    if (flushed) posthog.register(props)
}

export const queueEvent = (name: string, props: Props = {}) => {
    if (flushed) {
        posthog.capture(name, props)
        return
    }
    queued.push({ name, props })
}

const doIdentify = (id: string, props: Props) => {
    const previous = posthog.get_distinct_id()

    posthog.identify(id, props)

    // Repair, once, for anyone already sitting on the `+98…` person this bug
    // created: tell PostHog the two ids are the same human so their history
    // merges instead of starting over. Guarded on the numbers actually matching
    // so an id that is not this person's phone is never folded in, and
    // self-limiting — after the merge `get_distinct_id()` is already canonical,
    // so it cannot fire twice. Ordered after identify() because alias() refuses
    // to run when its first argument is the current person.
    if (previous && previous !== id && isSameMobile(previous, id)) {
        posthog.alias(previous, id)
    }
}

/**
 * Identify the person behind this session, whenever we learn who they are.
 *
 * Buffered until PostHog is live, so it is safe to call from bootstrap code
 * that runs before the first render.
 */
export const identifyPerson = (
    phone: string | null | undefined,
    props: Props = {}
) => {
    const id = toPersonId(phone)
    if (!id) return
    if (!flushed) {
        pendingIdentity = { id, props }
        return
    }
    doIdentify(id, props)
}

/** Called once PostHog is live. Safe to call more than once. */
export const flushAnalytics = () => {
    if (flushed) return
    flushed = true
    if (pendingIdentity) {
        doIdentify(pendingIdentity.id, pendingIdentity.props)
        pendingIdentity = null
    }
    if (Object.keys(superProps).length) posthog.register(superProps)
    queued.forEach(({ name, props }) => posthog.capture(name, props))
    queued = []
}
